/**
 * Quantity-mode (MDOT-24700) gate: says whether an investment's composition is already
 * expressed in quantities, from a summary the caller has in hand — no extra request.
 * Feature-owned so deleting the quantity feature removes it wholesale.
 */
import type { InvestmentActions, InvestmentStatus } from "@eu.mdotm.api/sphere";
import type { IUser } from "$root/functional-areas/user";

/**
 * True once the composition is in quantities, reading only a plain investment summary or a
 * projected-list row.
 *
 * Two signals, because the backend reports the flag in one state and the action in the other:
 * - `quantityMode` — carried by both the summary and the projected-list row of a portfolio whose
 *   composition is in quantities.
 * - a pending `REAL_SHARES_TRANSFORM` proposal — the state a conversion leaves behind. The plain
 *   summary says `quantityMode: false` here and only the ENHANCED summary knows better, so the
 *   action is what identifies it for free.
 *
 * The action is paired with the status on purpose: `actualAction` is the *last* action, so it
 * still reads `REAL_SHARES_TRANSFORM` after a rejected conversion — and a rejected portfolio is
 * back on weights and must be convertible again.
 *
 * The summary names that last action `actualAction` and a projected-list row names it `action`, so
 * `actualAction` is read first and `action` stands in where it is absent.
 */
export function isQuantityMode(
	summary: Parameters<typeof isPendingConversion>[0] & { quantityMode?: boolean },
): boolean {
	return summary.quantityMode === true || isPendingConversion(summary);
}

/**
 * True while a Convert-to-quantities proposal awaits acceptance: the portfolio is a proposal whose
 * last action is `REAL_SHARES_TRANSFORM`. See {@link isQuantityMode} for why the status is paired
 * with the action and why `action` stands in for `actualAction`.
 */
export function isPendingConversion(summary: {
	status?: InvestmentStatus;
	action?: InvestmentActions;
	actualAction?: InvestmentActions;
}): boolean {
	return summary.status === "PROPOSAL_READY" && (summary.actualAction ?? summary.action) === "REAL_SHARES_TRANSFORM";
}

/**
 * True when this user may convert THIS investment to quantities.
 *
 * Three independent conditions: the `PORTFOLIO_IN_QUANTITY` service the customer is entitled to,
 * a composition that is not in quantities already — converting one twice is meaningless — and a
 * drifting weighting method. A fixed-weight investment is rebalanced back to its target weights,
 * which a quantity composition has no way to express, so it is not convertible.
 * The service is read off the user rather than guarded at the route because the feature has no
 * page of its own; its one entry point is the portfolio's Edit menu.
 */
export function canConvertToQuantities(
	user: Pick<IUser, "availableServices">,
	summary: Parameters<typeof isQuantityMode>[0] & { fixedWeights?: boolean },
): boolean {
	return (
		user.availableServices.includes("PORTFOLIO_IN_QUANTITY") &&
		summary.fixedWeights !== true &&
		!isQuantityMode(summary)
	);
}

/**
 * True when this user may create a portfolio in quantities: the creation wizard offers the
 * Weights / Quantities choice only to a customer entitled to the `PORTFOLIO_IN_QUANTITY` service.
 */
export function canCreateInQuantities(user: Pick<IUser, "availableServices">): boolean {
	return user.availableServices.includes("PORTFOLIO_IN_QUANTITY");
}
